import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";
import { MdCancel } from "react-icons/md";

const BUTTON_SIZE = 70;
const THUMB_SIZE = 180;

// Items can be:
//   - File (new upload — pending)
//   - string URL (legacy — existing file URL only)
//   - { id, url, extension, name } (existing server file with id, used by
//     the new delivery-approval flow so individual files can be DELETEd).
const isPdf = (file) => {
  if (!file) return false;
  if (typeof file === "string") return /\.pdf(\?|$)/i.test(file);
  if (file instanceof File) {
    return (file.type || "").toLowerCase() === "application/pdf";
  }
  if (file.extension) return /pdf/i.test(file.extension);
  if (file.url) return /\.pdf(\?|$)/i.test(file.url);
  return false;
};

const isImage = (file) => {
  if (!file) return false;
  if (typeof file === "string")
    return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(file);
  if (file instanceof File) {
    return (file.type || "").toLowerCase().startsWith("image/");
  }
  if (file.extension) return /^image\//i.test(file.extension);
  if (file.url) return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(file.url);
  return false;
};

const fileUrl = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;
  if (file instanceof File) {
    try {
      return URL.createObjectURL(file);
    } catch {
      return "";
    }
  }
  return file.url || "";
};

const TILE_BASE_SX = {
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  flexShrink: 0,
  borderRadius: "10px",
  border: "1px solid #E5E7EB",
  backgroundColor: "#F9FAFB",
  position: "relative",
  overflow: "hidden",
};

const PdfThumb = ({ file, onRemove, readOnly }) => (
  <Box
    sx={{
      ...TILE_BASE_SX,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      "&:hover": { borderColor: "#4D6BF6" },
    }}
    onClick={() => {
      const url = fileUrl(file);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    }}
  >
    <PdfIcon style={{ width: 56, height: 56 }} />
    <Typography
      sx={{
        fontSize: 11,
        color: "#374151",
        mt: 1,
        fontFamily: "Inter Tight",
        fontWeight: 600,
      }}
    >
      PDF
    </Typography>
    {!readOnly && (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.();
        }}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          color: "#DC2626",
          padding: "0px",
          "&:hover": { backgroundColor: "#fff" },
        }}
      >
        <MdCancel sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
);

const ImageThumb = ({ file, onRemove, readOnly, onOpen }) => {
  const url = fileUrl(file);
  return (
    <Box
      onClick={() => url && onOpen?.()}
      sx={{
        ...TILE_BASE_SX,
        cursor: url ? "pointer" : "default",
        "&:hover": { borderColor: "#4D6BF6" },
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt="Upload"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : null}
      {!readOnly && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "rgba(255,255,255,0.95)",
            color: "#DC2626",
            padding: "0px",
            "&:hover": { backgroundColor: "#fff" },
          }}
        >
          <MdCancel sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

const ImageLightbox = ({ images, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    if (index === null) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") onPrev?.();
      else if (e.key === "ArrowRight") onNext?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, onClose, onPrev, onNext]);

  if (index === null || !images?.[index]) return null;
  const url = fileUrl(images[index]);
  const total = images.length;

  return (
    <Modal open onClose={onClose} keepMounted={false}>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
          p: { xs: 2, md: 6 },
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.12)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {total > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onPrev?.();
            }}
            sx={{
              position: "absolute",
              left: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.12)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        )}

        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: "min(1100px, 90vw)",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            component="img"
            src={url}
            alt={`Image ${index + 1}`}
            sx={{
              maxWidth: "100%",
              maxHeight: "78vh",
              objectFit: "contain",
              borderRadius: "8px",
              backgroundColor: "#000",
            }}
          />
          {total > 1 && (
            <Typography
              sx={{
                fontSize: 13,
                color: "rgba(255,255,255,0.85)",
                fontFamily: "Inter Tight",
              }}
            >
              {index + 1} / {total}
            </Typography>
          )}
        </Box>

        {total > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onNext?.();
            }}
            sx={{
              position: "absolute",
              right: { xs: 8, md: 24 },
              top: "50%",
              transform: "translateY(-50%)",
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.12)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        )}
      </Box>
    </Modal>
  );
};

const PlusButton = ({ onClick, disabled = false }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      width: BUTTON_SIZE,
      height: BUTTON_SIZE,
      flexShrink: 0,
      borderRadius: "12px",
      border: "1.5px dashed #D1D5DB",
      backgroundColor: "#F9FAFB",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      transition: "all 0.15s ease-in-out",
      "&:hover": disabled
        ? {}
        : {
            borderColor: "#4D6BF6",
            backgroundColor: "rgba(77,107,246,0.05)",
          },
    }}
  >
    <AddIcon sx={{ fontSize: 26, color: "#4D6BF6" }} />
  </Box>
);

export default function UploadGrid({
  title,
  subtitle,
  icon,
  pdfFiles = [],
  onPdfFilesChange,
  images = [],
  onImagesChange,
  allowImages = true,
  readOnly = false,
  maxPdfs,
  // When true, thumbnails on mobile (xs) lay out as a horizontal-scroll
  // strip instead of wrapping. Caller-opt-in so existing usages keep
  // their flex-wrap behaviour.
  mobileScroll = false,
}) {
  const accept = allowImages ? "application/pdf,image/*" : "application/pdf";
  const inputRef = useRef(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const pdfList = Array.isArray(pdfFiles) ? pdfFiles : [];
  const imagesList = images || [];
  const pdfCap = Number.isFinite(maxPdfs) ? maxPdfs : null;
  const pdfFull = pdfCap != null && pdfList.length >= pdfCap;

  const handleSelect = (files) => {
    const nextPdfs = [...pdfList];
    const nextImages = Array.isArray(imagesList) ? [...imagesList] : [];
    files.forEach((f) => {
      if (isPdf(f)) {
        if (pdfCap == null || nextPdfs.length < pdfCap) nextPdfs.push(f);
      } else if (isImage(f) && allowImages) {
        nextImages.push(f);
      }
    });
    if (nextPdfs.length !== pdfList.length) onPdfFilesChange?.(nextPdfs);
    if (allowImages && nextImages.length !== imagesList.length) {
      onImagesChange?.(nextImages);
    }
  };

  const handleInputChange = (e) => {
    const arr = Array.from(e.target.files || []);
    if (arr.length > 0) handleSelect(arr);
    e.target.value = "";
  };

  const handleRemovePdf = (idx) => {
    const next = pdfList.filter((_, i) => i !== idx);
    onPdfFilesChange?.(next);
  };
  const handleRemoveImage = (idx) => {
    const next = imagesList.filter((_, i) => i !== idx);
    onImagesChange?.(next);
  };

  const handlePrev = () => {
    if (!imagesList.length) return;
    setLightboxIndex((i) =>
      i === null ? null : (i - 1 + imagesList.length) % imagesList.length,
    );
  };
  const handleNext = () => {
    if (!imagesList.length) return;
    setLightboxIndex((i) => (i === null ? null : (i + 1) % imagesList.length));
  };

  const hasAny = pdfList.length > 0 || imagesList.length > 0;

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 12,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            mt: 0.25,
            mb: 2,
            ml: 3.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box sx={{ mb: 2 }} />}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        {!readOnly && (
          <PlusButton
            onClick={() => inputRef.current?.click()}
            disabled={pdfFull}
          />
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            // Mobile-scroll mode: thumbnails ride a horizontal-scroll
            // strip on xs, then revert to the regular wrapped grid on
            // md+. Non-opt-in callers keep the original flex-wrap.
            flexWrap: mobileScroll
              ? { xs: "nowrap", md: "wrap" }
              : "wrap",
            overflowX: mobileScroll ? { xs: "auto", md: "visible" } : "visible",
            gap: 1.5,
            alignContent: "flex-start",
            pb: mobileScroll ? { xs: 0.5, md: 0 } : 0,
            "& > *": mobileScroll
              ? { flexShrink: { xs: 0, md: 1 } }
              : undefined,
            "&::-webkit-scrollbar": mobileScroll
              ? { height: 6 }
              : undefined,
            "&::-webkit-scrollbar-thumb": mobileScroll
              ? { backgroundColor: "#E5E7EB", borderRadius: 3 }
              : undefined,
          }}
        >
          {pdfList.map((pdf, i) => (
            <PdfThumb
              key={`pdf-${i}`}
              file={pdf}
              readOnly={readOnly}
              onRemove={() => handleRemovePdf(i)}
            />
          ))}
          {imagesList.map((img, i) => (
            <ImageThumb
              key={i}
              file={img}
              readOnly={readOnly}
              onRemove={() => handleRemoveImage(i)}
              onOpen={() => setLightboxIndex(i)}
            />
          ))}
          {readOnly && !hasAny && (
            <Typography sx={{ fontSize: 13, color: "#9CA3AF", py: 1 }}>
              No files uploaded.
            </Typography>
          )}
        </Box>
      </Box>

      {!readOnly && (
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={pdfCap !== 1}
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
      )}

      <ImageLightbox
        images={imagesList}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </Box>
  );
}
